/**
 * Tool rail and object list (10-web-admin § 6.3).
 *
 * The rail is icons, as the board draws it — "the design's rail is unlabeled"
 * — with the name and the shortcut on the tooltip. The letters are the ones
 * the footer hint promises; the number keys 1–6 are the unambiguous
 * alternative, because `R` means "rotate" while something is selected and
 * "room" when nothing is.
 */

import { useTranslation } from 'react-i18next';
import { DEFAULT_SIZE_CM, type EditorObject } from '@cowoo/shared';
import { Icon, IconTile, type IconName } from '../../ui';

export const TOOLS = ['select', 'desk', 'room', 'locker', 'parking', 'office'] as const;
export type Tool = (typeof TOOLS)[number];

/** § 6.3 — the letter for each tool; the digit is its index plus one. */
export const TOOL_KEYS: Record<Tool, string> = {
  select: 'v',
  desk: 'd',
  room: 'r',
  locker: 'l',
  parking: 'p',
  office: 'o',
};

const TOOL_ICONS: Record<Tool, IconName> = {
  select: 'select',
  desk: 'desk',
  room: 'room',
  locker: 'locker',
  parking: 'parking',
  office: 'office',
};

/**
 * `R` is ambiguous by design in the spec: rotate with a selection, room
 * without one. Resolving it here keeps that decision out of the key handler.
 */
export function toolForKey(key: string, hasSelection: boolean): Tool | 'rotate' | null {
  const lower = key.toLowerCase();
  if (lower === 'r') return hasSelection ? 'rotate' : 'room';

  const digit = Number(key);
  if (Number.isInteger(digit) && digit >= 1 && digit <= TOOLS.length) return TOOLS[digit - 1] ?? null;

  const match = (Object.entries(TOOL_KEYS) as Array<[Tool, string]>).find(([, letter]) => letter === lower);
  return match?.[0] ?? null;
}

export function ToolRail({
  active,
  onSelect,
  zoom,
  onZoom,
  onRelabel,
}: {
  active: Tool;
  onSelect: (tool: Tool) => void;
  zoom: number;
  onZoom: (direction: 1 | -1) => void;
  /** The pencil: § 6.3's "Beschriftung bearbeiten", which focuses "Nummer". */
  onRelabel: () => void;
}) {
  const { t } = useTranslation();

  return (
    <nav
      className="border-line flex flex-col items-center gap-1.5 border-r bg-white py-3"
      aria-label={t('admin.editor.objects')}
    >
      {TOOLS.map((tool) => (
        <IconTile
          key={tool}
          icon={TOOL_ICONS[tool]}
          label={`${t(`admin.editor.tool.${tool}`)} · ${TOOL_KEYS[tool].toUpperCase()}`}
          active={active === tool}
          testId={`tool-${tool}`}
          onClick={() => onSelect(tool)}
        />
      ))}
      <span className="bg-line my-1.5 h-px w-7" />
      <IconTile icon="pencil" label={t('admin.editor.relabel')} onClick={onRelabel} />
      <div className="mt-auto flex flex-col items-center gap-1.5">
        <IconTile icon="plus" label={t('admin.editor.zoomIn')} onClick={() => onZoom(1)} />
        <span className="text-muted font-body text-[11px] tabular-nums">{`${Math.round(zoom * 100)} %`}</span>
        <IconTile icon="minus" label={t('admin.editor.zoomOut')} onClick={() => onZoom(-1)} />
      </div>
    </nav>
  );
}

/** § 6.3 — grouped by kind with counts; selecting here selects on the canvas. */
export function ObjectList({
  objects,
  selection,
  query,
  onQuery,
  onSelect,
}: {
  objects: EditorObject[];
  selection: string[];
  query: string;
  onQuery: (value: string) => void;
  onSelect: (ids: string[]) => void;
}) {
  const { t } = useTranslation();
  const needle = query.trim().toLowerCase();
  const matching = needle
    ? objects.filter(
        (object) =>
          object.label.toLowerCase().includes(needle) || (object.zone ?? '').toLowerCase().includes(needle),
      )
    : objects;

  return (
    <>
      <div className="border-line border-b px-4 pb-2.5 pt-3.5">
        <label className="border-line rounded-input text-placeholder flex h-9 items-center gap-2 border bg-white px-3">
          <Icon name="search" size={14} />
          <input
            value={query}
            onChange={(event) => onQuery(event.target.value)}
            placeholder={t('admin.editor.search')}
            aria-label={t('admin.editor.search')}
            className="text-adminBody text-ink placeholder:text-placeholder min-w-0 flex-1 bg-transparent outline-none"
          />
        </label>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto p-2 text-[13px]">
        {(Object.keys(DEFAULT_SIZE_CM) as Array<EditorObject['kind']>).map((kind) => {
          // Every kind is listed with its count, a zero included — the
          // board does, and a heading that appears the moment a first
          // locker is placed would move everything below it.
          const rows = matching.filter((object) => object.kind === kind);

          return (
            <section key={kind} className="contents">
              <h3 className="text-ink flex items-center justify-between rounded-[8px] px-2.5 py-2 font-semibold first:mt-0 [&:not(:first-child)]:mt-1.5">
                <span>{t(`admin.editor.group.${kind}`)}</span>
                <span className="text-muted tabular-nums">{rows.length}</span>
              </h3>
              {rows.map((object) => {
                const selected = selection.includes(object.resourceId);
                return (
                  <button
                    key={object.resourceId}
                    type="button"
                    data-testid={`list-${object.label}`}
                    aria-current={selected || undefined}
                    onClick={() => onSelect([object.resourceId])}
                    className={`flex w-full items-center gap-2 rounded-[8px] px-2.5 py-[7px] text-left ${
                      selected ? 'bg-teal-tint' : 'hover:bg-fog'
                    }`}
                  >
                    <StatusMark status={object.status} />
                    <span className="text-ink flex-1 truncate">
                      {kind === 'desk'
                        ? t('admin.editor.rowDesk', { label: object.label, zone: object.zone ?? '' })
                        : [object.label, object.zone].filter(Boolean).join(' · ')}
                    </span>
                    <span className="text-muted text-[11px]">
                      {object.status === 'bookable'
                        ? t('admin.editor.statusShortBookable')
                        : t('admin.editor.statusShortBlocked')}
                    </span>
                  </button>
                );
              })}
            </section>
          );
        })}
      </div>
    </>
  );
}

/**
 * The 8 px mark beside a row — a rounded *square*, as the board draws it, not
 * a dot. It repeats what the status word says; colour is never the only
 * signal (03 § 9).
 */
function StatusMark({ status }: { status: EditorObject['status'] }) {
  return (
    <span
      aria-hidden
      className={`inline-block h-2 w-2 flex-none rounded-[2px] ${status === 'bookable' ? 'bg-teal' : 'bg-disabled'}`}
    />
  );
}
